<template>
    <div>
        <component :is="mainContentWrapper.is" v-bind="mainContentWrapper">
            <template #additionally>
                <div class="flex-vertical-middle">
                    <div v-if="entry.cancel" class="button button-danger cursor-default margin-right-15">
                        Abgesagt
                    </div>

                    <router-link v-if="!isPopup && editable" :to="{name: resource + '-entry-edit', params: {id: entry.id, start: entry.recurrence_start}}"
                        class="material-icons md-24 additionally-icon">edit</router-link>
                </div>
            </template>

            <div class="row">
                <div class="col-12 col-md-4">
                    <span>Datum:</span>
                    <span v-html="entryTimeString"></span>
                </div>

                <div class="col-12 col-md-8" v-if="entry.location_text_title">
                    <span>Ort:</span>
                    <router-link v-if="entry.room_id" :to="{name: 'roommanagement-list', params: {'id': entry.room_id}}">
                        {{entry.location_text_title}}
                    </router-link>
                    <span v-else>{{entry.location_text_title}}</span>
                </div>
            </div>

            <div class="row default-margin-top">
                <div class="col-12 col-md-3" v-if="previewImage">
                    <a :href="previewImage.paths.huge!" :title="previewImage.description" v-light-box>
                        <img :src="previewImage.paths.preview!" :alt="'Bild ' + (previewImage.description || previewImage.filename_original)">
                    </a>
                </div>

                <div class="col-12" :class="{'col-md-9': previewImage}">
                    <div class="row">
                        <div class="col-12 col-md-6" v-if="entry.clients.length || entry.links.length">
                            <template v-if="entry.clients.length">
                                <div :class="{'spacer-15': viewSize.smDown}"><strong>Ansprechpartner</strong></div>
                                <div class="spacer-5">
                                    <person-box-user v-for="user in entry.clients" :id="user" :key="'event-client-'+user"/>
                                </div>
                            </template>

                            <template v-if="entry.links.length">
                                <div :class="{'spacer-15': entry.clients.length || viewSize.smDown}"><strong>Links</strong></div>
                                <div class="spacer-5">
                                    <div v-for="link in entry.links" :key="'event-link-'+link.link_id">
                                        <a :href="link.url" :title="link.link_description" :target="isExternalLink(link.url) ? '_blank' : '_self'">
                                            <i class="material-icons md-18" aria-hidden="true" role="img">open_in_new</i>
                                            <span>{{link.link_description}}</span>
                                        </a>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <div class="col-12 col-md-6" :class="{'spacer-15': viewSize.smDown}">
                            <div><strong>Kategorie</strong></div>
                            <div class="colour-badge">
                                <div class="colour-badge-preview" :style="{backgroundColor: '#' + entry.category_color}"></div>
                                <div class="colour-badge-text">
                                    {{entry.category_name}}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="entry.description" class="default-margin-top message-content fr-view">
                <div><strong>Beschreibung</strong></div>
                <div class="spacer-5" v-html="entry.description"></div>
            </div>

            <div v-if="entry.description_additional" class="default-margin-top message-content fr-view">
                <div><strong>Zusätzliche Informationen:</strong></div>
                <div class="spacer-5" v-html="entry.description_additional"></div>
            </div>

            <div v-if="entry.files && entry.files.length">
                <hr class="portuna-box-full-content-width">

                <div><strong>Dokumente</strong></div>
                <file-list :files="entry.files">
                    <template #additional>
                        <app-popup-download
                            v-if="entry.files.length > 1"
                            :create-route="`/event/entry/create-zip-files/id/${entry.id}`"
                            :download-route="'/event/entry/download-zip-file'"
                            class="col-12 col-lg-6 flex-vertical-middle">
                            <template #link="{downloadFiles}">
                                <a class="button button-secondary button-block spacer-10" @click="downloadFiles">
                                    <i class="material-icons" aria-hidden="true" role="img">file_download</i>
                                    <span>Alle Dateien herunterladen</span>
                                </a>
                            </template>
                        </app-popup-download>
                    </template>
                </file-list>
            </div>
        </component>

        <portuna-box v-if="entry.invitations && entry.invitations.length" title="Einladungen" title-icon="person"
            class="no-padding default-margin-top">
            <app-invitations v-model="entry.invitations" entry-type="Termin" :entry-title="entry.title" :allow-like="canLike" :all-count="entry.all_user_count" :is-popup="isPopup"
                :end-date="entry.likeable_end" :show-status="1 === entry.likeable" :response-url="'/event/entry/response/id/' + entry.id + '/start/' + entry.recurrence_start"/>
        </portuna-box>

        <portuna-box v-if="hasGalleries || entry.images.length" title="Bilder" title-icon="image">
            <div v-if="hasGalleries">
                <div><strong>Zugehörige Galerien</strong></div>
                <div class="row spacer-15">
                    <image-list-box v-for="gallery in entry.gallery_data" :key="'event-gallery-' + gallery.id" :link="{name: 'gallery-view', params: {id: gallery.id}}"
                        :image="`/data/image/${gallery.path}medium/${gallery.filename}`" box-height="high" :alt="gallery.title" :title="gallery.title" :cover="listViewCover"
                        class="col-6 col-md-4 col-xl-3">
                        <template #title>{{gallery.title}}</template>

                        <template #additional-information>
                            <span v-tooltip="'Anzahl der Bilder in dieser Galerie'">
                                <i class="material-icons md-16" aria-hidden="true" role="img">insert_photo</i>
                                {{gallery.image_count}}
                            </span>
                            &nbsp;
                            <span v-if="gallery.date" v-tooltip="'Datum der Fotoaufnahmen'">
                                <i class="material-icons md-16" aria-hidden="true" role="img">event</i>
                                {{datetime(gallery.date, 'd.m.Y')}}
                            </span>
                        </template>
                    </image-list-box>
                </div>
            </div>
            <div v-if="entry.images.length">
                <div><strong>Zugehörige Bilder</strong></div>
                <div class="row spacer-15">
                    <image-list-box v-for="image in entry.images" :key="'event-image-' + image.id" :link="image.paths.huge" target="_blank"
                        :image="image.paths.preview" box-height="high" :cover="listViewCover" class="col-6 col-md-4 col-xl-3"
                        :alt="image.description || image.filename_original" :title="image.description || image.filename_original" light-box>
                        <template #title>
                            <span>{{image.description || image.filename_original}}</span>
                        </template>
                    </image-list-box>
                </div>
            </div>
        </portuna-box>
    </div>
</template>

<script setup lang="ts">
    import {useCalendar} from '@/composables/modules/calendar/calendar';
    import AppInvitations from '@/components/AppInvitations.vue';
    import AppPopupDownload from '@/components/AppPopupDownload.vue';
    import FileList from '@/components/FileList.vue';
    import ImageListBox from '@/components/ImageListBox.vue';
    import PersonBoxUser from '@/components/PersonBoxUser.vue';
    import PortunaBox from '@/components/PortunaBox.vue';
    import datetime from '@/filters/datetime';
    import {getEntryTimeString} from '@/library/modules/calendar/calendar';
    import {isAllowedModule} from '@/library/user';
    import {isExternalLink} from '@/utils/portuna';
    import {useStore} from 'vuex';
    import {useRoute} from 'vue-router';
    import {computed, toRefs} from 'vue';

    const props = withDefaults(
        defineProps<{
            resource: CalendarResources;
            additionalData?: AdditionalCalendarData;
            entry: EventEntryData;
            calendar: CurrentCalendarData | CalendarData;
            editable?: boolean | number;
        }>(),
        {
            additionalData: () => ({}),
            editable: false,
        },
    );

    const {
              resource,
              entry,
              editable,
          } = toRefs(props);

    const {
              today,
              options,
          } = useCalendar(resource);

    const store = useStore();
    const route = useRoute();

    const viewSize = store.state.viewSize;

    const listViewCover = computed<boolean>(() => store.state.module.gallery.listViewCover);

    const entryTimeString = computed(() =>
    {
        return getEntryTimeString(entry.value!, false);
    });

    const isPopup = computed((): boolean =>
    {
        return route.name !== 'event-entry-view';
    });

    const previewImage = computed((): false | ImageUpload =>
    {
        let preview: false | ImageUpload = false;
        if (!isPopup.value && entry.value!.images.length)
        {
            for (let image of entry.value!.images)
            {
                if (image.id === entry.value!.preview)
                {
                    preview = image;
                }
            }
        }

        return preview;
    });

    const mainContentWrapper = computed(() =>
    {
        if (isPopup.value)
        {
            return {
                is: 'div',
            };
        }

        return {
            is: PortunaBox,
            title: entry.value!.title,
            titleIcon: options.value!.calendarIcon,
        };
    });

    const canLike = computed(() =>
    {
        return !!(
            entry.value!.likeable
            && (
                !entry.value!.likeable_end ||
                today.value! <= entry.value!.likeable_end
            )
        );
    });

    const hasGalleries = computed(() =>
    {
        return isAllowedModule('gallery') && entry.value!.gallery_data && entry.value!.gallery_data.length;
    });

    const fetchGallerySettings = async (options: GallerySettingsFetchOptions): Promise<any> => store.dispatch('module/gallery/fetch', options);

    if (isAllowedModule('gallery') && entry.value!.gallery_data?.length)
    {
        fetchGallerySettings({setLoading: false}).then();
    }
</script>